import { useEffect, useMemo, useState } from "react";
import {
  BadgeIndianRupee,
  Calculator,
  Download,
  FileText,
  Landmark,
  Plus,
  Printer,
  ReceiptText,
  RotateCcw,
  Users,
} from "lucide-react";
import { toast } from "sonner";

import api, { API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Breadcrumbs,
  EmptyState,
  Loading,
  PageHeader,
  StatCard,
  WorkflowGuide,
  inr,
  useTabParam,
} from "../../components/shared";
import { useSocietyFeatures } from "../../context/SocietyFeatureContext";

const FINANCE_TABS = ["billing", "collections", "charges", "assign", "reports", "p3-reports"];

const can = (context, module, action) =>
  Boolean(context?.permissions?.[module]?.includes(action));

const newKey = () =>
  window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

export default function SocietyFinance() {
  const [context, setContext] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [charges, setCharges] = useState([]);
  const [rules, setRules] = useState([]);
  const [assignments, setAssignments] = useState({ units: [], charges: [] });
  const [structure, setStructure] = useState({ buildings: [], wings: [] });
  const [periods, setPeriods] = useState([]);
  const [bills, setBills] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [units, setUnits] = useState([]);
  const [report, setReport] = useState(null);
  const [printPreference, setPrintPreference] = useState({ mode: "BILL_ONLY" });
  const [preview, setPreview] = useState(null);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [editCharge, setEditCharge] = useState(null);
  const [ruleCharge, setRuleCharge] = useState(null);
  const [rulePreview, setRulePreview] = useState(null);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [singleBillPeriod, setSingleBillPeriod] = useState(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [openingOpen, setOpeningOpen] = useState(false);
  const [adjustmentBill, setAdjustmentBill] = useState(null);
  const [advanceReceipt, setAdvanceReceipt] = useState(null);
  const [ledgerPerson, setLedgerPerson] = useState(null);
  const { byKey } = useSocietyFeatures();
  const [tabParam, setFinanceTab] = useTabParam("billing");
  const financeTab = FINANCE_TABS.includes(tabParam) ? tabParam : "billing";
  const featureOn = (key) => Boolean(byKey?.(key)?.allowed);

  const load = async () => {
    try {
      const [ctx, dash, chargeRows, ruleRows, periodRows, billRows, receiptRows, unitRows, outstanding, structureData, printSettings, assignmentData] =
        await Promise.all([
          api.get("/society-setup/context"),
          api.get("/society-finance/dashboard"),
          api.get("/society-finance/charges"),
          api.get("/society-finance/billing-rules"),
          api.get("/society-finance/periods"),
          api.get("/society-finance/bills"),
          api.get("/society-finance/receipts"),
          api.get("/society-directory/units", { params: { limit: 100 } }),
          api.get("/society-finance/reports/outstanding"),
          api.get("/society-setup/structure").catch(() => ({ data: { buildings: [], wings: [] } })),
          api.get("/society-finance/print-preferences"),
          api.get("/society-finance/assignments").catch(() => ({ data: { units: [], charges: [] } })),
        ]);
      setContext(ctx.data);
      setDashboard(dash.data);
      setCharges(chargeRows.data);
      setRules(ruleRows.data);
      setStructure(structureData.data);
      setPeriods(periodRows.data);
      setBills(billRows.data);
      setReceipts(receiptRows.data);
      setUnits(unitRows.data.items || []);
      setReport(outstanding.data);
      setPrintPreference(printSettings.data);
      setAssignments(assignmentData.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load maintenance finance");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const partyOptions = useMemo(() => {
    const seen = new Map();
    bills.forEach((bill) => {
      const personId = bill.party?.person_id;
      if (personId) {
        seen.set(personId, bill.party);
      }
    });
    return [...seen.values()];
  }, [bills]);

  const openPreview = async (period) => {
    try {
      const response = await api.get(`/society-finance/periods/${period.period_id}/preview`);
      setPreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create bill preview");
    }
  };

  const openRulePreview = async (rule) => {
    try {
      const response = await api.get(
        `/society-finance/billing-rules/${rule.rule_id}/eligible-units`,
      );
      setRulePreview(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not preview eligible Units");
    }
  };

  if (!dashboard || !report) {
    return <Loading />;
  }

  return (
    <div data-testid="society-finance-page">
      <Breadcrumbs
        items={[
          { label: "Dashboard", to: "/app" },
          { label: "Finance & Accounting" },
          { label: "Maintenance Finance" },
        ]}
      />
      <PageHeader
        title="Maintenance Finance"
        subtitle="Society-only maintenance billing, collections, outstanding, and operational records."
      >
        {can(context, "finance_collections", "create") && (
          <Button data-testid="finance-record-receipt-button" onClick={() => setReceiptOpen(true)}>
            <ReceiptText className="h-4 w-4" />
            Record receipt
          </Button>
        )}
      </PageHeader>

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={BadgeIndianRupee}
          label="Billed"
          testid="finance-billed-kpi"
          value={inr(dashboard.billed)}
        />
        <StatCard
          icon={Landmark}
          label="Collected"
          testid="finance-collected-kpi"
          tone="secondary"
          value={inr(dashboard.collected)}
        />
        <StatCard
          icon={FileText}
          label="Outstanding"
          testid="finance-outstanding-kpi"
          tone="rose"
          value={inr(dashboard.outstanding)}
        />
        <StatCard
          icon={ReceiptText}
          label="Advance credit"
          testid="finance-advance-kpi"
          tone="amber"
          value={inr(dashboard.advances)}
        />
      </section>

      <Tabs className="mt-8" onValueChange={setFinanceTab} value={financeTab}>
        <TabsList className="h-auto flex-wrap" data-testid="finance-tabs">
          <TabsTrigger data-testid="finance-billing-tab" value="billing">Billing</TabsTrigger>
          <TabsTrigger data-testid="finance-collections-tab" value="collections">
            Collections
          </TabsTrigger>
          <TabsTrigger data-testid="finance-charges-tab" value="charges">Charge master</TabsTrigger>
          <TabsTrigger data-testid="finance-assign-tab" value="assign">Assign charges</TabsTrigger>
          <TabsTrigger data-testid="finance-reports-tab" value="reports">Outstanding</TabsTrigger>
          <TabsTrigger data-testid="finance-p3-reports-tab" value="p3-reports">Report centre</TabsTrigger>
        </TabsList>

        <TabsContent value="billing">
          <BillingPanel
            bills={bills}
            canAdjust={can(context, "finance_collections", "edit")}
            canCreate={can(context, "finance_billing", "create")}
            canConfigurePrintPreference={can(context, "finance_settings", "edit")}
            onAdjust={setAdjustmentBill}
            onBillUnit={setSingleBillPeriod}
            onOpenPreview={openPreview}
            onPeriod={() => setPeriodOpen(true)}
            onPrintPreferenceSaved={setPrintPreference}
            periods={periods}
            printPreference={printPreference}
          />
        </TabsContent>

        <TabsContent value="collections">
          <CollectionsPanel
            canCreate={can(context, "finance_collections", "create")}
            canEdit={can(context, "finance_collections", "edit")}
            onApplyCredit={setAdvanceReceipt}
            onOpening={() => setOpeningOpen(true)}
            receipts={receipts}
            onReload={load}
          />
        </TabsContent>

        <TabsContent value="charges">
          <ChargePanel
            canCreate={can(context, "finance_settings", "create")}
            canEdit={can(context, "finance_settings", "edit")}
            charges={charges}
            onCreate={() => setChargeOpen(true)}
            onEdit={setEditCharge}
            onPreview={openRulePreview}
            onToggle={async (charge) => {
              try {
                await api.patch(`/society-finance/charges/${charge.charge_id}`, {
                  active: !charge.active,
                });
                toast.success(charge.active ? "Charge deactivated" : "Charge activated");
                load();
              } catch (error) {
                toast.error(error.response?.data?.detail || "Could not update charge");
              }
            }}
            onVersion={setRuleCharge}
            rules={rules}
          />
        </TabsContent>

        <TabsContent value="assign">
          <AssignPanel
            canEdit={can(context, "finance_settings", "edit")}
            data={assignments}
            onSaved={load}
            structure={structure}
          />
        </TabsContent>

        <TabsContent value="reports">
          <OutstandingPanel onLedger={setLedgerPerson} report={report} />
        </TabsContent>
        <TabsContent value="p3-reports">
          <P3ReportsPanel units={units} />
        </TabsContent>
      </Tabs>

      <WorkflowGuide
        steps={[
          {
            label: "Configure Units & Structure",
            to: "/app/setup?tab=structure",
            icon: Landmark,
            testid: "finance-guide-setup",
            show: can(context, "profile", "view"),
          },
          {
            label: "Members & Units",
            to: "/app/directory",
            icon: Users,
            testid: "finance-guide-directory",
            show: can(context, "member_master", "view") && featureOn("sm02_directory"),
          },
          {
            label: "Report centre",
            to: "/app/finance?tab=p3-reports",
            icon: FileText,
            testid: "finance-guide-reports",
            show: can(context, "finance_reports", "view"),
          },
          {
            label: "Society Accounting",
            to: "/app/accounting",
            icon: Calculator,
            testid: "finance-guide-accounting",
            show: can(context, "accounting_settings", "view") && featureOn("sm04_accounting"),
          },
        ]}
        title="Continue the Unit-to-Ledger workflow"
      />

      <ChargeDialog
        charges={charges}
        onOpenChange={setChargeOpen}
        onSaved={load}
        open={chargeOpen}
        structure={structure}
        units={units}
      />
      <ChargeEditDialog
        charge={editCharge}
        onOpenChange={setEditCharge}
        onSaved={load}
      />
      <RuleDialog
        charge={ruleCharge}
        charges={charges}
        onOpenChange={setRuleCharge}
        onSaved={load}
        structure={structure}
        units={units}
      />
      <PeriodDialog onOpenChange={setPeriodOpen} onSaved={load} open={periodOpen} />
      <SingleBillDialog
        onOpenChange={setSingleBillPeriod}
        onSaved={load}
        period={singleBillPeriod}
        units={units}
      />
      <ReceiptDialog
        bills={bills}
        onOpenChange={setReceiptOpen}
        onSaved={load}
        open={receiptOpen}
        parties={partyOptions}
      />
      <OpeningDialog
        onOpenChange={setOpeningOpen}
        onSaved={load}
        open={openingOpen}
        units={units}
      />
      <AdjustmentDialog
        bill={adjustmentBill}
        onOpenChange={setAdjustmentBill}
        onSaved={load}
      />
      <ApplyCreditDialog
        bills={bills}
        onOpenChange={setAdvanceReceipt}
        onSaved={load}
        receipt={advanceReceipt}
      />
      <LedgerDialog onOpenChange={setLedgerPerson} person={ledgerPerson} />
      <PreviewDialog onOpenChange={setPreview} onPosted={load} preview={preview} />
      <RulePreviewDialog onOpenChange={setRulePreview} preview={rulePreview} />
    </div>
  );
}

function useRowSelection(items, idKey) {
  const [selected, setSelected] = useState(() => new Set());
  const ids = items.map((item) => item[idKey]);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const toggle = (id) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected(() => (allSelected ? new Set() : new Set(ids)));
  const clear = () => setSelected(new Set());
  const chosen = ids.filter((id) => selected.has(id));
  return { selected, chosen, allSelected, toggle, toggleAll, clear };
}

async function openBatchPdf(path, payload, onError) {
  try {
    const response = await api.post(path, payload, { responseType: "blob" });
    const url = URL.createObjectURL(response.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return true;
  } catch (error) {
    let detail = "Could not generate the combined PDF";
    if (error.response?.data instanceof Blob) {
      try {
        detail = JSON.parse(await error.response.data.text()).detail || detail;
      } catch {
        /* keep default */
      }
    }
    (onError || toast.error)(detail);
    return false;
  }
}

function BillingPanel({
  bills,
  canAdjust,
  canCreate,
  canConfigurePrintPreference,
  onAdjust,
  onBillUnit,
  onOpenPreview,
  onPeriod,
  onPrintPreferenceSaved,
  periods,
  printPreference,
}) {
  const { selected, chosen, allSelected, toggle, toggleAll, clear } = useRowSelection(
    bills,
    "bill_id",
  );
  const [printMode, setPrintMode] = useState(printPreference?.mode || "BILL_ONLY");
  useEffect(() => {
    setPrintMode(printPreference?.mode || "BILL_ONLY");
  }, [printPreference?.mode]);
  const printSelected = async () => {
    const printed = await openBatchPdf("/pdf/society-finance/bills/print", {
      bill_ids: chosen,
      mode: printMode,
    });
    if (printed) {
      clear();
    }
  };
  const savePrintPreference = async () => {
    try {
      const response = await api.put("/society-finance/print-preferences", { mode: printMode });
      onPrintPreferenceSaved(response.data);
      toast.success("Society maintenance print preference saved");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save print preference");
    }
  };
  const printOne = async (bill) => {
    await openBatchPdf("/pdf/society-finance/bills/print", {
      bill_ids: [bill.bill_id],
      mode: printMode,
    });
  };
  return (
    <section className="mt-5" data-testid="finance-billing-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Billing periods</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Preview unit snapshots before posting. Posted bills are never silently rewritten.
          </p>
        </div>
        {canCreate && (
          <Button data-testid="finance-create-period-button" onClick={onPeriod}>
            <Plus className="h-4 w-4" />
            New billing period
          </Button>
        )}
      </div>
      <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {periods.length === 0 ? (
          <EmptyState icon={FileText} title="Create a billing period to begin" />
        ) : (
          periods.map((period) => (
            <article
              className="border bg-card p-4 shadow-sm"
              data-testid={`finance-period-${period.period_id}`}
              key={period.period_id}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display font-bold">{period.label}</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {period.start_date} to {period.end_date} · Due {period.due_date}
                  </p>
                </div>
                <Status status={period.status} testId={`finance-period-status-${period.period_id}`} />
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  data-testid={`finance-preview-period-${period.period_id}`}
                  onClick={() => onOpenPreview(period)}
                  size="sm"
                  variant="outline"
                >
                  {period.status === "POSTED" ? "Preview posted bills" : "Generate all units"}
                </Button>
                {canCreate && period.status !== "POSTED" && (
                  <Button
                    data-testid={`finance-bill-unit-${period.period_id}`}
                    onClick={() => onBillUnit(period)}
                    size="sm"
                    variant="ghost"
                  >
                    Bill one unit
                  </Button>
                )}
              </div>
            </article>
          ))
        )}
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display font-bold">Bills register</h3>
        {bills.length > 0 && (
          <div className="flex items-center gap-3">
            <select
              data-testid="finance-configured-print-mode"
              onChange={(event) => setPrintMode(event.target.value)}
              value={printMode}
            >
              <option value="BILL_ONLY">Maintenance bill only</option>
              <option value="RECEIPT_ONLY">Previous-month receipt(s) only</option>
              <option value="COMBINED">Combined bill + previous-month receipt(s)</option>
            </select>
            <span className="text-sm text-muted-foreground" data-testid="finance-bills-selected-count">
              {chosen.length} selected
            </span>
            <Button
              data-testid="finance-print-bills-button"
              disabled={chosen.length === 0}
              onClick={printSelected}
              size="sm"
              variant="outline"
            >
              <Printer className="h-4 w-4" />
              Print configured ({chosen.length})
            </Button>
            {canConfigurePrintPreference && (
              <Button
                data-testid="finance-save-print-preference-button"
                onClick={savePrintPreference}
                size="sm"
                variant="ghost"
              >
                Save Society default
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="mt-3 overflow-x-auto border bg-card" data-testid="finance-bills-table">
        <table className="min-w-[940px] w-full text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="p-3 text-left">
                <input
                  aria-label="Select all bills"
                  checked={allSelected}
                  data-testid="finance-bills-select-all"
                  onChange={toggleAll}
                  type="checkbox"
                />
              </th>
              <th className="p-3 text-left">Bill</th>
              <th className="p-3 text-left">Unit / party</th>
              <th className="p-3 text-left">Due</th>
              <th className="p-3 text-right">Total</th>
              <th className="p-3 text-right">Outstanding</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Record</th>
            </tr>
          </thead>
          <tbody>
            {bills.length === 0 ? (
              <tr><td className="p-8 text-center text-muted-foreground" colSpan={8}>No posted bills.</td></tr>
            ) : bills.map((bill) => (
              <tr className="border-t" data-testid={`finance-bill-${bill.bill_id}`} key={bill.bill_id}>
                <td className="p-3">
                  <input
                    aria-label={`Select bill ${bill.bill_number}`}
                    checked={selected.has(bill.bill_id)}
                    data-testid={`finance-bill-select-${bill.bill_id}`}
                    onChange={() => toggle(bill.bill_id)}
                    type="checkbox"
                  />
                </td>
                <td className="p-3 font-mono text-xs">{bill.bill_number}</td>
                <td className="p-3">
                  <div className="font-medium">{bill.unit_snapshot?.unit_number}</div>
                  <div className="text-xs text-muted-foreground">{bill.party?.name}</div>
                </td>
                <td className="p-3">{bill.due_date}</td>
                <td className="p-3 text-right">{inr(bill.total)}</td>
                <td className="p-3 text-right font-medium">{inr(bill.outstanding)}</td>
                <td className="p-3"><Status status={bill.status} testId={`finance-bill-status-${bill.bill_id}`} /></td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      data-testid={`finance-bill-configured-pdf-${bill.bill_id}`}
                      onClick={() => printOne(bill)}
                      size="icon"
                      title="Open configured maintenance document"
                      variant="ghost"
                    >
                      <ReceiptText className="h-4 w-4" />
                    </Button>
                    <Button
                      data-testid={`finance-bill-pdf-${bill.bill_id}`}
                      onClick={() => window.open(`${API}/pdf/society-finance/bill/${bill.bill_id}`, "_blank")}
                      size="icon"
                      title="Open maintenance bill PDF"
                      variant="ghost"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    {canAdjust && bill.status !== "PAID" && (
                      <Button
                        data-testid={`finance-adjust-bill-${bill.bill_id}`}
                        onClick={() => onAdjust(bill)}
                        size="sm"
                        variant="outline"
                      >
                        Adjust
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CollectionsPanel({ canCreate, canEdit, onApplyCredit, onOpening, onReload, receipts }) {
  const { selected, chosen, allSelected, toggle, toggleAll, clear } = useRowSelection(
    receipts,
    "receipt_id",
  );
  const printSelected = async () => {
    await openBatchPdf("/pdf/society-finance/receipts/batch", { receipt_ids: chosen });
    clear();
  };
  const reverse = async (receipt) => {
    const reason = window.prompt("Reason for reversal");
    if (!reason?.trim()) {
      return;
    }
    try {
      await api.post(`/society-finance/receipts/${receipt.receipt_id}/reverse`, { reason });
      toast.success("Receipt reversed; linked bill balances were restored");
      onReload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not reverse receipt");
    }
  };

  return (
    <section className="mt-5" data-testid="finance-collections-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Receipts and credits</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Automatic allocation uses oldest open bills first; manual allocations are also supported.
          </p>
        </div>
        {canCreate && (
          <Button data-testid="finance-opening-balance-button" onClick={onOpening} variant="outline">
            <Plus className="h-4 w-4" />
            Opening balance
          </Button>
        )}
      </div>
      <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-display font-bold">Receipts register</h3>
        {receipts.length > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground" data-testid="finance-receipts-selected-count">
              {chosen.length} selected
            </span>
            <Button
              data-testid="finance-print-receipts-button"
              disabled={chosen.length === 0}
              onClick={printSelected}
              size="sm"
              variant="outline"
            >
              <Printer className="h-4 w-4" />
              Print selected ({chosen.length})
            </Button>
          </div>
        )}
      </div>
      <div className="mt-3 overflow-x-auto border bg-card" data-testid="finance-receipts-table">
        <table className="min-w-[880px] w-full text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="p-3 text-left">
                <input
                  aria-label="Select all receipts"
                  checked={allSelected}
                  data-testid="finance-receipts-select-all"
                  onChange={toggleAll}
                  type="checkbox"
                />
              </th>
              <th className="p-3 text-left">Receipt</th>
              <th className="p-3 text-left">Party</th>
              <th className="p-3 text-left">Mode / reference</th>
              <th className="p-3 text-right">Received</th>
              <th className="p-3 text-right">Advance</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Record</th>
            </tr>
          </thead>
          <tbody>
            {receipts.length === 0 ? (
              <tr><td className="p-8 text-center text-muted-foreground" colSpan={8}>No receipts recorded.</td></tr>
            ) : receipts.map((receipt) => (
              <tr className="border-t" data-testid={`finance-receipt-${receipt.receipt_id}`} key={receipt.receipt_id}>
                <td className="p-3">
                  <input
                    aria-label={`Select receipt ${receipt.receipt_number}`}
                    checked={selected.has(receipt.receipt_id)}
                    data-testid={`finance-receipt-select-${receipt.receipt_id}`}
                    onChange={() => toggle(receipt.receipt_id)}
                    type="checkbox"
                  />
                </td>
                <td className="p-3 font-mono text-xs">{receipt.receipt_number}</td>
                <td className="p-3">{receipt.party?.name}</td>
                <td className="p-3">{receipt.mode}<span className="text-muted-foreground"> · {receipt.reference || "—"}</span></td>
                <td className="p-3 text-right">{inr(receipt.amount)}</td>
                <td className="p-3 text-right">{inr(receipt.unallocated_credit)}</td>
                <td className="p-3"><Status status={receipt.status} testId={`finance-receipt-status-${receipt.receipt_id}`} /></td>
                <td className="p-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      data-testid={`finance-receipt-pdf-${receipt.receipt_id}`}
                      onClick={() => window.open(`${API}/pdf/society-finance/receipt/${receipt.receipt_id}`, "_blank")}
                      size="icon"
                      title="Open maintenance receipt PDF"
                      variant="ghost"
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    {canEdit && receipt.status === "POSTED" && (
                      <Button
                        data-testid={`finance-apply-credit-${receipt.receipt_id}`}
                        disabled={!receipt.unallocated_credit}
                        onClick={() => onApplyCredit(receipt)}
                        size="sm"
                        variant="outline"
                      >
                        Apply credit
                      </Button>
                    )}
                    {canEdit && receipt.status === "POSTED" && (
                      <Button
                        data-testid={`finance-reverse-receipt-${receipt.receipt_id}`}
                        onClick={() => reverse(receipt)}
                        size="icon"
                        title="Reverse receipt"
                        variant="ghost"
                      >
                        <RotateCcw className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const METHOD_LABELS = {
  FIXED: "Fixed amount",
  PER_SQFT: "Per Sq.Ft.",
  PER_QUANTITY: "Per parking slot",
  UNIT_TYPE_RATE: "Per unit type",
  PERCENTAGE: "Percentage",
  VARIABLE: "Variable",
};

function ChargePanel({ canCreate, canEdit, charges, onCreate, onEdit, onPreview, onToggle, onVersion, rules }) {
  const chargeName = (id) => charges.find((item) => item.charge_id === id)?.name || "base charge";
  const latestRule = (chargeId) =>
    rules.filter((rule) => rule.charge_id === chargeId).sort((a, b) => (b.effective_from || "").localeCompare(a.effective_from || ""))[0];
  const ordered = [...charges].sort(
    (a, b) => (a.bill_display_order || 999) - (b.bill_display_order || 999) || a.name.localeCompare(b.name),
  );
  return (
    <section className="mt-5" data-testid="finance-charge-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Charges Master</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Define every society charge, its calculation method and bill order. Rate changes never alter posted bills.
          </p>
        </div>
        {canCreate && (
          <Button data-testid="finance-create-charge-button" onClick={onCreate}>
            <Plus className="h-4 w-4" />
            Add charge
          </Button>
        )}
      </div>
      {charges.length === 0 ? (
        <div className="mt-5">
          <EmptyState icon={BadgeIndianRupee} title="No configured maintenance charges" />
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto border bg-card" data-testid="finance-charges-table">
          <table className="min-w-[920px] w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-3 text-left">Charge name</th>
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Calculation method</th>
                <th className="p-3 text-left">Rate / value</th>
                <th className="p-3 text-left">Frequency</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {ordered.map((charge) => {
                const rule = latestRule(charge.charge_id);
                const method = METHOD_LABELS[charge.calculation_basis] || charge.calculation_basis;
                let value = "—";
                if (charge.calculation_basis === "PERCENTAGE" && rule?.percentage) {
                  value = `${rule.percentage}% of ${chargeName(rule.percentage_base_charge_id)}`;
                } else if (charge.calculation_basis === "VARIABLE") {
                  value = "Per unit / period";
                } else if (charge.calculation_basis === "UNIT_TYPE_RATE") {
                  value = "By unit type";
                } else if (charge.current_rate != null) {
                  const areaLabel = rule?.area_basis ? ` / ${rule.area_basis.replace(/_/g, " ").toLowerCase()}` : "";
                  const suffix = charge.calculation_basis === "PER_SQFT" ? (areaLabel || " / sq.ft.") : charge.calculation_basis === "PER_QUANTITY" ? " / slot" : "";
                  value = `${inr(charge.current_rate)}${suffix}`;
                }
                return (
                  <tr className="border-t align-top" data-testid={`finance-charge-${charge.charge_id}`} key={charge.charge_id}>
                    <td className="p-3 font-medium">
                      {charge.name}
                      {charge.description ? <p className="mt-0.5 text-xs text-muted-foreground">{charge.description}</p> : null}
                    </td>
                    <td className="p-3 font-mono text-xs">{charge.charge_code || "—"}</td>
                    <td className="p-3">{method}</td>
                    <td className="p-3 capitalize">{value}</td>
                    <td className="p-3">{rule?.frequency || "—"}</td>
                    <td className="p-3">
                      <Status
                        status={charge.active ? "ACTIVE" : "INACTIVE"}
                        testId={`finance-charge-status-${charge.charge_id}`}
                      />
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap justify-end gap-2">
                        {canEdit && (
                          <Button
                            data-testid={`finance-edit-charge-${charge.charge_id}`}
                            onClick={() => onEdit(charge)}
                            size="sm"
                            variant="outline"
                          >
                            Edit
                          </Button>
                        )}
                        {rule && (
                          <Button
                            data-testid={`finance-preview-rule-${rule.rule_id}`}
                            onClick={() => onPreview(rule)}
                            size="sm"
                            variant="ghost"
                          >
                            Eligible Units
                          </Button>
                        )}
                        {canCreate && (
                          <Button
                            data-testid={`finance-version-charge-${charge.charge_id}`}
                            onClick={() => onVersion(charge)}
                            size="sm"
                            variant="outline"
                          >
                            Add rule
                          </Button>
                        )}
                        {canEdit && (
                          <Button
                            data-testid={`finance-toggle-charge-${charge.charge_id}`}
                            onClick={() => onToggle(charge)}
                            size="sm"
                            variant="ghost"
                          >
                            {charge.active ? "Deactivate" : "Activate"}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AssignPanel({ canEdit, data, onSaved, structure }) {
  const units = data?.units || [];
  const editableCharges = (data?.charges || []).filter((charge) => charge.editable);
  const readOnlyCharges = (data?.charges || []).filter((charge) => !charge.editable);
  const buildings = new Map((structure?.buildings || []).map((item) => [item.building_id, item]));
  const [local, setLocal] = useState({});
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("");

  useEffect(() => {
    const next = {};
    (data?.charges || []).forEach((charge) => {
      if (charge.editable) next[charge.charge_id] = new Set(charge.assigned_unit_ids || []);
    });
    setLocal(next);
  }, [data]);

  const filteredUnits = units.filter((unit) => {
    if (buildingFilter && unit.building_id !== buildingFilter) return false;
    if (search) {
      const needle = search.toLowerCase();
      const hay = `${unit.unit_number} ${unit.building} ${unit.wing} ${unit.floor}`.toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });

  const toggle = (chargeId, unitId) => {
    setLocal((prev) => {
      const set = new Set(prev[chargeId] || []);
      if (set.has(unitId)) set.delete(unitId);
      else set.add(unitId);
      return { ...prev, [chargeId]: set };
    });
  };

  const toggleColumn = (chargeId, on) => {
    setLocal((prev) => {
      const set = new Set(prev[chargeId] || []);
      filteredUnits.forEach((unit) => (on ? set.add(unit.unit_id) : set.delete(unit.unit_id)));
      return { ...prev, [chargeId]: set };
    });
  };

  const columnAllChecked = (chargeId) =>
    filteredUnits.length > 0 && filteredUnits.every((unit) => (local[chargeId] || new Set()).has(unit.unit_id));

  const save = async () => {
    const changed = editableCharges.filter((charge) => {
      const before = [...(charge.assigned_unit_ids || [])].sort().join(",");
      const after = [...(local[charge.charge_id] || [])].sort().join(",");
      return before !== after;
    });
    if (changed.length === 0) {
      toast.info("No assignment changes to save");
      return;
    }
    try {
      for (const charge of changed) {
        await api.put(`/society-finance/assignments/${charge.rule_id}`, {
          unit_ids: [...(local[charge.charge_id] || [])],
        });
      }
      toast.success(`Updated assignments for ${changed.length} charge(s)`);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save assignments");
    }
  };

  return (
    <section className="mt-5" data-testid="finance-assign-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Assign charges to Units</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Tick which charges apply to which Units. Changes affect future bills only — posted bills never change.
          </p>
        </div>
        {canEdit && (
          <Button data-testid="finance-assign-save" onClick={save}>
            Save assignments
          </Button>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-3">
        <Input
          className="max-w-xs"
          data-testid="finance-assign-search"
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search Unit / building / wing"
          value={search}
        />
        <select
          data-testid="finance-assign-building"
          onChange={(event) => setBuildingFilter(event.target.value)}
          value={buildingFilter}
        >
          <option value="">All buildings</option>
          {[...new Set(units.map((unit) => unit.building_id).filter(Boolean))].map((id) => (
            <option key={id} value={id}>{buildings.get(id)?.name || id}</option>
          ))}
        </select>
      </div>

      {editableCharges.length === 0 ? (
        <div className="mt-5">
          <EmptyState icon={BadgeIndianRupee} title="No assignable recurring charges" subtitle="Create a recurring Fixed, Per-Sq-Ft, Unit-Type or Percentage charge first." />
        </div>
      ) : (
        <div className="mt-5 overflow-x-auto border bg-card" data-testid="finance-assign-matrix">
          <table className="min-w-[640px] w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="sticky left-0 bg-muted/60 p-3 text-left">Unit</th>
                {editableCharges.map((charge) => (
                  <th className="p-3 text-center" key={charge.charge_id}>
                    <div className="font-medium">{charge.name}</div>
                    {canEdit && (
                      <label className="mt-1 flex items-center justify-center gap-1 text-xs font-normal text-muted-foreground">
                        <input
                          checked={columnAllChecked(charge.charge_id)}
                          data-testid={`finance-assign-selectall-${charge.charge_id}`}
                          onChange={(event) => toggleColumn(charge.charge_id, event.target.checked)}
                          type="checkbox"
                        />
                        All
                      </label>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUnits.length === 0 ? (
                <tr><td className="p-8 text-center text-muted-foreground" colSpan={editableCharges.length + 1}>No Units match the filter.</td></tr>
              ) : filteredUnits.map((unit) => (
                <tr className="border-t" data-testid={`finance-assign-row-${unit.unit_id}`} key={unit.unit_id}>
                  <td className="sticky left-0 bg-card p-3">
                    <div className="font-medium">{unit.unit_number}</div>
                    <div className="text-xs text-muted-foreground">{[unit.building, unit.wing].filter(Boolean).join(" · ")}</div>
                  </td>
                  {editableCharges.map((charge) => (
                    <td className="p-3 text-center" key={charge.charge_id}>
                      <input
                        checked={(local[charge.charge_id] || new Set()).has(unit.unit_id)}
                        data-testid={`finance-assign-cell-${charge.charge_id}-${unit.unit_id}`}
                        disabled={!canEdit}
                        onChange={() => toggle(charge.charge_id, unit.unit_id)}
                        type="checkbox"
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {readOnlyCharges.length > 0 && (
        <div className="mt-4 border bg-muted/20 p-4 text-sm" data-testid="finance-assign-readonly">
          <p className="font-medium">Charges managed outside this matrix</p>
          <ul className="mt-2 space-y-1 text-muted-foreground">
            {readOnlyCharges.map((charge) => (
              <li data-testid={`finance-assign-readonly-${charge.charge_id}`} key={charge.charge_id}>
                <b>{charge.name}</b> — {charge.scope === "SOCIETY" ? "applies society-wide (no rule criteria)" : charge.scope === "CRITERIA" ? "assigned by building/wing/type/occupancy criteria in its rule" : charge.frequency === "ONE_TIME" ? "one-time charge, applied on selected Units" : "per-parking charge"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function OutstandingPanel({ onLedger, report }) {
  return (
    <section className="mt-5" data-testid="finance-outstanding-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">Outstanding and aging</h2>
          <p className="mt-1 text-sm text-muted-foreground">Total open maintenance dues: {inr(report.total)}</p>
        </div>
        <ReportExports kind="outstanding" />
      </div>
      <div className="mt-5 overflow-x-auto border bg-card" data-testid="finance-outstanding-table">
        <table className="min-w-[840px] w-full text-sm">
          <thead className="bg-muted/60">
            <tr>
              <th className="p-3 text-left">Bill</th>
              <th className="p-3 text-left">Unit / party</th>
              <th className="p-3 text-left">Due</th>
              <th className="p-3 text-left">Aging</th>
              <th className="p-3 text-right">Outstanding</th>
              <th className="p-3 text-right">Ledger</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.length === 0 ? (
              <tr><td className="p-8 text-center text-muted-foreground" colSpan={6}>No outstanding maintenance dues.</td></tr>
            ) : report.rows.map((row) => (
              <tr className="border-t" data-testid={`finance-outstanding-${row.bill_id}`} key={row.bill_id}>
                <td className="p-3 font-mono text-xs">{row.bill_number}</td>
                <td className="p-3">{row.unit_snapshot?.unit_number} · {row.party?.name}</td>
                <td className="p-3">{row.due_date}</td>
                <td className="p-3">{row.aging_bucket} ({row.days_overdue} days)</td>
                <td className="p-3 text-right font-medium">{inr(row.outstanding)}</td>
                <td className="p-3 text-right">
                  {row.party?.person_id && (
                    <Button
                      data-testid={`finance-party-ledger-${row.bill_id}`}
                      onClick={() => onLedger(row.party)}
                      size="sm"
                      variant="outline"
                    >
                      View
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function P3ReportsPanel({ units }) {
  const [unitId, setUnitId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [view, setView] = useState(null);
  const [asOf, setAsOf] = useState("");

  const load = async (kind) => {
    try {
      const routes = {
        aging: "/society-finance/reports/aging",
        bills: "/society-finance/reports/bill-register",
        collections: "/society-finance/reports/collection",
        receipts: "/society-finance/reports/receipt-register",
        summary: "/society-finance/reports/bill-vs-collection",
        ledger: `/society-finance/reports/unit-ledger/${unitId}`,
        member: `/society-finance/reports/member-statement/${memberId}`,
        noDues: `/society-finance/reports/no-dues/eligibility/${unitId}`,
      };
      if ((kind === "ledger" || kind === "noDues") && !unitId) {
        toast.error("Choose a Unit first");
        return;
      }
      if (kind === "member" && !memberId) {
        toast.error("Enter a Member ID first");
        return;
      }
      const response = await api.get(routes[kind], { params: asOf ? { as_of: asOf } : {} });
      setView({ kind, data: response.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not load this report");
    }
  };

  const issueNoDues = async () => {
    try {
      const response = await api.post("/society-finance/reports/no-dues", {
        unit_id: unitId,
        as_of: asOf,
      });
      setView({ kind: "noDues", data: { ...view?.data, certificate: response.data } });
      toast.success(`No-Dues certificate ${response.data.certificate_number} issued`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "No-Dues cannot be issued");
    }
  };

  const rows = view?.data?.rows || [];
  return (
    <section className="mt-5 space-y-5" data-testid="finance-p3-report-centre">
      <div className="border bg-card p-5">
        <h2 className="font-display text-xl font-bold">Financial visibility</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Derived from SM-03 bills, allocations, adjustments, openings, advances, and reversals.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Input
            className="w-auto"
            data-testid="finance-report-as-of"
            onChange={(event) => setAsOf(event.target.value)}
            type="date"
            value={asOf}
          />
          <select
            data-testid="finance-report-unit"
            onChange={(event) => setUnitId(event.target.value)}
            value={unitId}
          >
            <option value="">Choose Unit for ledger / No-Dues</option>
            {units.map((unit) => <option key={unit.unit_id} value={unit.unit_id}>{unit.unit_number}</option>)}
          </select>
          <Input
            className="w-52"
            data-testid="finance-report-member"
            onChange={(event) => setMemberId(event.target.value)}
            placeholder="Member ID for statement"
            value={memberId}
          />
          {[
            ["aging", "Aging"],
            ["bills", "Bill register"],
            ["receipts", "Receipt register"],
            ["collections", "Collection report"],
            ["summary", "Bill vs collection"],
            ["ledger", "Unit ledger"],
            ["member", "Member statement"],
            ["noDues", "No-Dues check"],
          ].map(([kind, label]) => (
            <Button
              data-testid={`finance-p3-${kind}-button`}
              key={kind}
              onClick={() => load(kind)}
              size="sm"
              variant={kind === "ledger" || kind === "noDues" ? "default" : "outline"}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>
      {view && <>
        <ReportResult data={view.data} kind={view.kind} onIssueNoDues={issueNoDues} />
        <ReportExports
          asOf={asOf}
          kind={view.kind}
          memberId={memberId}
          unitId={unitId}
        />
      </>}
      {rows.length > 0 && <div className="overflow-auto border bg-card" data-testid="finance-p3-report-table">
        <table className="min-w-[900px] w-full text-sm">
          <thead className="bg-muted/60"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Reference</th><th className="p-3 text-left">Details</th><th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th><th className="p-3 text-right">Balance</th></tr></thead>
          <tbody>{rows.map((row, index) => <tr className="border-t" data-testid={`finance-p3-row-${index}`} key={`${row.source_record_id || row.bill_id || row.receipt_id}-${index}`}><td className="p-3">{row.date || row.bill_date || "—"}</td><td className="p-3 font-mono text-xs">{row.reference || row.bill_number || row.receipt_number || "—"}</td><td className="p-3">{row.description || row.transaction_type || row.party?.name || row.unit_snapshot?.unit_number}</td><td className="p-3 text-right">{row.debit ? inr(row.debit) : "—"}</td><td className="p-3 text-right">{row.credit ? inr(row.credit) : "—"}</td><td className="p-3 text-right">{row.running_balance !== undefined ? inr(row.running_balance) : row.balance !== undefined ? inr(row.balance) : "—"}</td></tr>)}</tbody>
        </table>
      </div>}
    </section>
  );
}

function ReportExports({ asOf, kind, memberId, unitId }) {
  const reportNames = {
    aging: "aging",
    bills: "bill-register",
    collections: "collection",
    ledger: "unit-ledger",
    member: "member-statement",
    outstanding: "outstanding",
    receipts: "receipt-register",
    summary: "bill-vs-collection",
  };
  const name = reportNames[kind];
  if (!name) return null;
  const query = new URLSearchParams();
  if (asOf) query.set("as_of", asOf);
  if (unitId && name === "unit-ledger") query.set("unit_id", unitId);
  if (memberId && name === "member-statement") query.set("person_id", memberId);
  return (
    <div className="flex flex-wrap gap-2" data-testid="finance-report-exports">
      {["csv", "xlsx"].map((format) => (
        <a
          data-testid={`finance-report-export-${name}-${format}`}
          href={`${API}/society-finance/reports/exports/${name}.${format}?${query}`}
          key={format}
        >
          <Button size="sm" variant="outline">
            <Download className="h-4 w-4" />
            {format.toUpperCase()}
          </Button>
        </a>
      ))}
    </div>
  );
}

function ReportResult({ data, kind, onIssueNoDues }) {
  if (kind === "noDues") {
    return <section className="border bg-card p-5" data-testid="finance-no-dues-result"><h3 className="font-display font-bold">No-Dues eligibility</h3><p className="mt-2 text-sm">{data.eligible ? "Eligible: no payable balance as of the selected date." : `Blocked: ${inr(data.position?.payable)} remains payable.`}</p>{data.certificate && <a data-testid="finance-no-dues-pdf" href={`${API}/society-finance/reports/no-dues/${data.certificate.certificate_id}/pdf`}><Button className="mt-3" size="sm">Open certificate PDF</Button></a>}{data.eligible && !data.certificate && <Button className="mt-3" data-testid="finance-issue-no-dues" onClick={onIssueNoDues} size="sm">Issue No-Dues</Button>}</section>;
  }
  if (kind === "ledger") {
    return <section className="grid gap-3 border bg-card p-5 sm:grid-cols-3" data-testid="finance-unit-ledger-summary"><div><p className="text-xs text-muted-foreground">Unit</p><p className="font-medium">{data.unit?.unit_number}</p></div><div><p className="text-xs text-muted-foreground">Closing position</p><p className="font-medium">{data.position?.label} {inr(data.position?.payable || data.position?.credit)}</p></div><div><p className="text-xs text-muted-foreground">As of</p><p className="font-medium">{data.as_of}</p></div></section>;
  }
  return <section className="border bg-card p-5" data-testid="finance-p3-summary"><h3 className="font-display font-bold">{kind.replaceAll("-", " ")}</h3><pre className="mt-3 overflow-auto text-xs">{JSON.stringify(Object.fromEntries(Object.entries(data).filter(([key]) => key !== "rows")), null, 2)}</pre></section>;
}

const emptyRuleForm = (basis = "FIXED") => ({
  area_basis: "SOCIETY_DEFAULT",
  bill_display_order: "",
  building_ids: [],
  calculation_basis: basis,
  charge_code: "",
  description: "",
  effective_from: "",
  effective_to: "",
  frequency: "MONTHLY",
  occupancy_statuses: [],
  parking_slot_types: [],
  percentage: "",
  percentage_base_charge_id: "",
  rate: "",
  requires_active_parking: false,
  unit_ids: [],
  unit_type_rates_text: "",
  unit_types: [],
  wing_ids: [],
});

const selectedValues = (event) => [...event.target.selectedOptions].map((option) => option.value);

const rulePayload = (form) => {
  const unitTypeRates = {};
  form.unit_type_rates_text.split(",").forEach((entry) => {
    const [type, rate] = entry.split(":").map((part) => part.trim());
    if (type && rate) {
      unitTypeRates[type.toUpperCase()] = rate;
    }
  });
  const isPercentage = form.calculation_basis === "PERCENTAGE";
  const isVariable = form.calculation_basis === "VARIABLE";
  return {
    area_basis: form.area_basis,
    calculation_basis: form.calculation_basis,
    charge_code: form.charge_code,
    description: form.description,
    bill_display_order: form.bill_display_order,
    effective_from: form.effective_from,
    effective_to: form.effective_to,
    frequency: form.frequency,
    name: form.name,
    rate: isPercentage || isVariable ? 0 : form.rate,
    percentage: isPercentage ? form.percentage : undefined,
    percentage_base_charge_id: isPercentage ? form.percentage_base_charge_id : undefined,
    unit_type_rates: unitTypeRates,
    eligibility: {
      building_ids: form.building_ids,
      occupancy_statuses: form.occupancy_statuses,
      parking_slot_types: form.parking_slot_types,
      requires_active_parking: form.requires_active_parking,
      unit_ids: form.unit_ids,
      unit_types: form.unit_types,
      wing_ids: form.wing_ids,
    },
  };
};

function RuleFields({ charges = [], form, setForm, structure, testPrefix, units }) {
  const buildingIds = [...new Set(units.map((unit) => unit.building_id).filter(Boolean))];
  const wingIds = [...new Set(units.map((unit) => unit.wing_id).filter(Boolean))];
  const unitTypes = [...new Set(units.map((unit) => unit.unit_type).filter(Boolean))];
  const buildings = new Map((structure?.buildings || []).map((item) => [item.building_id, item]));
  const wings = new Map((structure?.wings || []).map((item) => [item.wing_id, item]));
  const isPercentage = form.calculation_basis === "PERCENTAGE";
  const isVariable = form.calculation_basis === "VARIABLE";
  const baseChargeOptions = charges.filter(
    (charge) => charge.calculation_basis !== "PERCENTAGE" && charge.active,
  );

  return (
    <div className="grid gap-3">
      <select
        data-testid={`${testPrefix}-basis`}
        onChange={(event) => setForm({ ...form, calculation_basis: event.target.value })}
        value={form.calculation_basis}
      >
        <option value="FIXED">Fixed per eligible unit</option>
        <option value="PER_SQFT">Per square foot</option>
        <option value="PER_QUANTITY">Per active parking allocation</option>
        <option value="UNIT_TYPE_RATE">Different rate by Unit Type</option>
        <option value="PERCENTAGE">Percentage of another charge</option>
        <option value="VARIABLE">Variable amount (entered per unit each period)</option>
      </select>
      <select
        data-testid={`${testPrefix}-frequency`}
        onChange={(event) => setForm({ ...form, frequency: event.target.value })}
        value={form.frequency}
      >
        <option value="MONTHLY">Monthly</option>
        <option value="BI_MONTHLY">Bi-monthly</option>
        <option value="QUARTERLY">Quarterly</option>
        <option value="HALF_YEARLY">Half-yearly</option>
        <option value="ANNUAL">Annual</option>
        <option value="ONE_TIME">One-time selected Units</option>
      </select>
      {form.calculation_basis === "PER_SQFT" && (
        <select
          data-testid={`${testPrefix}-area-basis`}
          onChange={(event) => setForm({ ...form, area_basis: event.target.value })}
          value={form.area_basis}
        >
          <option value="SOCIETY_DEFAULT">Society default area basis</option>
          <option value="CARPET_AREA">Carpet Area</option>
          <option value="BUILT_UP_AREA">Built-up Area</option>
          <option value="SUPER_BUILT_UP_AREA">Super Built-up Area</option>
          <option value="CUSTOM_CHARGEABLE_AREA">Custom Chargeable Area</option>
        </select>
      )}
      {isPercentage ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            data-testid={`${testPrefix}-percentage`}
            min="0"
            max="100"
            onChange={(event) => setForm({ ...form, percentage: event.target.value })}
            placeholder="Percentage (e.g. 10)"
            type="number"
            value={form.percentage}
          />
          <select
            data-testid={`${testPrefix}-percentage-base`}
            onChange={(event) => setForm({ ...form, percentage_base_charge_id: event.target.value })}
            value={form.percentage_base_charge_id}
          >
            <option value="">Choose base charge</option>
            {baseChargeOptions.map((charge) => (
              <option key={charge.charge_id} value={charge.charge_id}>{charge.name}</option>
            ))}
          </select>
        </div>
      ) : isVariable ? (
        <p className="border bg-muted/30 p-3 text-xs text-muted-foreground" data-testid={`${testPrefix}-variable-note`}>
          Variable charges carry no fixed rate. Enter the amount for each unit against a draft billing period before posting.
        </p>
      ) : (
        <Input
          data-testid={`${testPrefix}-rate`}
          min="0"
          onChange={(event) => setForm({ ...form, rate: event.target.value })}
          placeholder="Rate"
          type="number"
          value={form.rate}
        />
      )}
      {form.calculation_basis === "UNIT_TYPE_RATE" && (
        <Input
          data-testid={`${testPrefix}-unit-type-rates`}
          onChange={(event) => setForm({ ...form, unit_type_rates_text: event.target.value })}
          placeholder="FLAT:100, SHOP:250"
          value={form.unit_type_rates_text}
        />
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <select
          data-testid={`${testPrefix}-buildings`}
          multiple
          onChange={(event) => setForm({ ...form, building_ids: selectedValues(event) })}
          value={form.building_ids}
        >
          {buildingIds.map((id) => (
            <option key={id} value={id}>
              {buildings.get(id)?.name || buildings.get(id)?.building_code || id}
            </option>
          ))}
        </select>
        <select
          data-testid={`${testPrefix}-wings`}
          multiple
          onChange={(event) => setForm({ ...form, wing_ids: selectedValues(event) })}
          value={form.wing_ids}
        >
          {wingIds.map((id) => (
            <option key={id} value={id}>{wings.get(id)?.name || id}</option>
          ))}
        </select>
        <select
          data-testid={`${testPrefix}-unit-types`}
          multiple
          onChange={(event) => setForm({ ...form, unit_types: selectedValues(event) })}
          value={form.unit_types}
        >
          {unitTypes.map((type) => <option key={type} value={type}>{type}</option>)}
        </select>
        <select
          data-testid={`${testPrefix}-occupancy`}
          multiple
          onChange={(event) => setForm({ ...form, occupancy_statuses: selectedValues(event) })}
          value={form.occupancy_statuses}
        >
          <option value="SELF_OCCUPIED">Self occupied</option>
          <option value="TENANT_OCCUPIED">Tenant occupied</option>
          <option value="VACANT">Vacant</option>
          <option value="OTHER">Other</option>
        </select>
      </div>
      <select
        data-testid={`${testPrefix}-units`}
        multiple
        onChange={(event) => setForm({ ...form, unit_ids: selectedValues(event) })}
        value={form.unit_ids}
      >
        {units.map((unit) => <option key={unit.unit_id} value={unit.unit_id}>{unit.unit_number}</option>)}
      </select>
      <label className="flex items-center gap-2 text-sm">
        <input
          checked={form.requires_active_parking}
          data-testid={`${testPrefix}-parking-required`}
          onChange={(event) => setForm({
            ...form,
            requires_active_parking: event.target.checked,
          })}
          type="checkbox"
        />
        Require active parking allocation
      </label>
    </div>
  );
}

function ChargeDialog({ charges, onOpenChange, onSaved, open, structure, units }) {
  const [form, setForm] = useState({ ...emptyRuleForm(), name: "" });
  const save = async () => {
    try {
      const payload = rulePayload(form);
      const charge = await api.post("/society-finance/charges", payload);
      await api.post(`/society-finance/charges/${charge.data.charge_id}/rates`, payload);
      await api.post("/society-finance/billing-rules", {
        ...payload,
        charge_id: charge.data.charge_id,
      });
      toast.success("Charge, rate, and versioned billing rule created");
      setForm({ ...emptyRuleForm(), name: "" });
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create charge");
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="finance-charge-dialog">
        <DialogHeader>
          <DialogTitle>Add maintenance charge</DialogTitle>
          <DialogDescription>
            Define a safe, effective-dated rule. Preview eligible Units before posting.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Input
            data-testid="finance-charge-name"
            onChange={(event) => setForm({ ...form, name: event.target.value })}
            placeholder="Charge name"
            value={form.name}
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              data-testid="finance-charge-code"
              onChange={(event) => setForm({ ...form, charge_code: event.target.value })}
              placeholder="Charge code (optional)"
              value={form.charge_code}
            />
            <Input
              data-testid="finance-charge-order"
              onChange={(event) => setForm({ ...form, bill_display_order: event.target.value })}
              placeholder="Bill display order"
              type="number"
              value={form.bill_display_order}
            />
            <Input
              data-testid="finance-charge-description"
              onChange={(event) => setForm({ ...form, description: event.target.value })}
              placeholder="Description (optional)"
              value={form.description}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              data-testid="finance-charge-effective-date"
              onChange={(event) => setForm({ ...form, effective_from: event.target.value })}
              type="date"
              value={form.effective_from}
            />
            <Input
              data-testid="finance-charge-effective-to"
              onChange={(event) => setForm({ ...form, effective_to: event.target.value })}
              type="date"
              value={form.effective_to}
            />
          </div>
          <RuleFields
            charges={charges}
            form={form}
            setForm={setForm}
            structure={structure}
            testPrefix="finance-charge"
            units={units}
          />
          <Button data-testid="finance-save-charge" onClick={save}>Save charge</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ChargeEditDialog({ charge, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ name: "", charge_code: "", description: "", bill_display_order: "" });
  useEffect(() => {
    if (charge) {
      setForm({
        name: charge.name || "",
        charge_code: charge.charge_code || "",
        description: charge.description || "",
        bill_display_order: charge.bill_display_order ?? "",
      });
    }
  }, [charge?.charge_id]);
  const save = async () => {
    try {
      await api.patch(`/society-finance/charges/${charge.charge_id}`, {
        name: form.name,
        charge_code: form.charge_code,
        description: form.description,
        bill_display_order: form.bill_display_order === "" ? 0 : Number(form.bill_display_order),
      });
      toast.success("Charge updated");
      onOpenChange(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not update charge");
    }
  };
  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(charge)}>
      <DialogContent data-testid="finance-charge-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit charge · {charge?.name}</DialogTitle>
          <DialogDescription>
            Editing the definition does not change posted bills. Add a rule version to change rates.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Input data-testid="finance-edit-charge-name" onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Charge name" value={form.name} />
          <Input data-testid="finance-edit-charge-code" onChange={(event) => setForm({ ...form, charge_code: event.target.value })} placeholder="Charge code" value={form.charge_code} />
          <Input data-testid="finance-edit-charge-order" onChange={(event) => setForm({ ...form, bill_display_order: event.target.value })} placeholder="Bill display order" type="number" value={form.bill_display_order} />
          <Input data-testid="finance-edit-charge-description" onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Description" value={form.description} />
          <Button data-testid="finance-save-charge-edit" onClick={save}>Save changes</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RuleDialog({ charge, charges, onOpenChange, onSaved, structure, units }) {
  const [form, setForm] = useState(emptyRuleForm(charge?.calculation_basis || "FIXED"));

  useEffect(() => {
    setForm(emptyRuleForm(charge?.calculation_basis || "FIXED"));
  }, [charge?.charge_id]);

  const save = async () => {
    try {
      const payload = rulePayload(form);
      await api.post(`/society-finance/charges/${charge.charge_id}/rates`, payload);
      await api.post("/society-finance/billing-rules", {
        ...payload,
        charge_id: charge.charge_id,
      });
      toast.success("Rule version created; the prior version is closed automatically");
      onOpenChange(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create rule version");
    }
  };

  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(charge)}>
      <DialogContent className="max-w-3xl" data-testid="finance-rule-dialog">
        <DialogHeader>
          <DialogTitle>Version rule · {charge?.name}</DialogTitle>
          <DialogDescription>
            A new effective period is created; posted bills retain their original snapshots.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              data-testid="finance-rule-effective-from"
              onChange={(event) => setForm({ ...form, effective_from: event.target.value })}
              type="date"
              value={form.effective_from}
            />
            <Input
              data-testid="finance-rule-effective-to"
              onChange={(event) => setForm({ ...form, effective_to: event.target.value })}
              type="date"
              value={form.effective_to}
            />
          </div>
          <RuleFields
            charges={charges}
            form={form}
            setForm={setForm}
            structure={structure}
            testPrefix="finance-rule"
            units={units}
          />
          <Button data-testid="finance-save-rule" onClick={save}>Create rule version</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PeriodDialog({ onOpenChange, onSaved, open }) {
  const [form, setForm] = useState({ due_date: "", end_date: "", label: "", start_date: "" });
  const save = async () => {
    try {
      await api.post("/society-finance/periods", form);
      toast.success("Billing period created; preview it before posting");
      setForm({ due_date: "", end_date: "", label: "", start_date: "" });
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not create billing period");
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent data-testid="finance-period-dialog">
        <DialogHeader>
          <DialogTitle>New billing period</DialogTitle>
          <DialogDescription>The period remains a draft until you post its reviewed preview.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Input data-testid="finance-period-label" onChange={(event) => setForm({ ...form, label: event.target.value })} placeholder="April 2026" value={form.label} />
          <Input data-testid="finance-period-start" onChange={(event) => setForm({ ...form, start_date: event.target.value })} type="date" value={form.start_date} />
          <Input data-testid="finance-period-end" onChange={(event) => setForm({ ...form, end_date: event.target.value })} type="date" value={form.end_date} />
          <Input data-testid="finance-period-due" onChange={(event) => setForm({ ...form, due_date: event.target.value })} type="date" value={form.due_date} />
          <Button data-testid="finance-save-period" onClick={save}>Create draft period</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReceiptDialog({ bills, onOpenChange, onSaved, open, parties }) {
  const [form, setForm] = useState({ amount: "", mode: "Bank transfer", party_person_id: "", reference: "" });
  const [manual, setManual] = useState(false);
  const [allocations, setAllocations] = useState([{ amount: "", bill_id: "" }]);
  const partyBills = bills.filter((bill) => bill.party?.person_id === form.party_person_id && bill.outstanding > 0);
  const save = async () => {
    try {
      const payload = { ...form, idempotency_key: newKey() };
      if (manual) {
        payload.allocations = allocations.filter((item) => item.bill_id && item.amount);
      }
      const response = await api.post("/society-finance/receipts", payload);
      toast.success(`Receipt ${response.data.receipt_number} recorded`);
      setForm({ amount: "", mode: "Bank transfer", party_person_id: "", reference: "" });
      setAllocations([{ amount: "", bill_id: "" }]);
      setManual(false);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not record receipt");
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="max-w-2xl" data-testid="finance-receipt-dialog">
        <DialogHeader>
          <DialogTitle>Record maintenance receipt</DialogTitle>
          <DialogDescription>Use automatic oldest-first allocation or direct a receipt across selected bills.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <select data-testid="finance-receipt-party" onChange={(event) => setForm({ ...form, party_person_id: event.target.value })} value={form.party_person_id}>
            <option value="">Choose billed party</option>
            {parties.map((party) => <option key={party.person_id} value={party.person_id}>{party.name}</option>)}
          </select>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input data-testid="finance-receipt-amount" min="0.01" onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="Amount" type="number" value={form.amount} />
            <select data-testid="finance-receipt-mode" onChange={(event) => setForm({ ...form, mode: event.target.value })} value={form.mode}>
              {["Cash", "UPI", "NEFT", "IMPS", "Bank transfer", "Cheque"].map((mode) => <option key={mode} value={mode}>{mode}</option>)}
            </select>
          </div>
          <Input data-testid="finance-receipt-reference" onChange={(event) => setForm({ ...form, reference: event.target.value })} placeholder="Reference / cheque / UTR" value={form.reference} />
          <label className="flex items-center gap-2 text-sm">
            <input checked={manual} data-testid="finance-receipt-manual-toggle" onChange={(event) => setManual(event.target.checked)} type="checkbox" />
            Allocate manually
          </label>
          {manual && <div className="space-y-2 border bg-muted/20 p-3" data-testid="finance-manual-allocations">
            {allocations.map((allocation, index) => <div className="flex gap-2" key={index}>
              <select data-testid={`finance-allocation-bill-${index}`} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, bill_id: event.target.value } : item))} value={allocation.bill_id}>
                <option value="">Choose bill</option>
                {partyBills.map((bill) => <option key={bill.bill_id} value={bill.bill_id}>{bill.bill_number} · {inr(bill.outstanding)}</option>)}
              </select>
              <Input data-testid={`finance-allocation-amount-${index}`} min="0.01" onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} placeholder="Amount" type="number" value={allocation.amount} />
            </div>)}
            <Button data-testid="finance-add-allocation" onClick={() => setAllocations([...allocations, { amount: "", bill_id: "" }])} size="sm" variant="outline">Add bill allocation</Button>
          </div>}
          <Button data-testid="finance-save-receipt" onClick={save}>Record receipt</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OpeningDialog({ onOpenChange, onSaved, open, units }) {
  const [form, setForm] = useState({ amount: "", balance_type: "DEBIT", date: "", unit_id: "" });
  const [job, setJob] = useState(null);
  const save = async () => {
    try {
      await api.post("/society-finance/opening-balances", form);
      toast.success("Opening balance posted");
      setForm({ amount: "", balance_type: "DEBIT", date: "", unit_id: "" });
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post opening balance");
    }
  };
  const validateImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const data = new FormData();
    data.append("file", file);
    try {
      const response = await api.post("/society-finance/opening-balances/import/validate", data);
      setJob(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not validate opening balance import");
    }
    event.target.value = "";
  };
  const confirmImport = async () => {
    try {
      await api.post(`/society-finance/opening-balances/import/${job.import_job_id}/confirm`);
      toast.success("Opening balance import completed");
      setJob(null);
      onOpenChange(false);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not import opening balances");
    }
  };
  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent data-testid="finance-opening-dialog">
        <DialogHeader>
          <DialogTitle>Post opening balance</DialogTitle>
          <DialogDescription>
            Debit means dues carried into this operational ledger; credit means opening advance.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <select data-testid="finance-opening-unit" onChange={(event) => setForm({ ...form, unit_id: event.target.value })} value={form.unit_id}>
            <option value="">Choose unit</option>
            {units.map((unit) => <option key={unit.unit_id} value={unit.unit_id}>{unit.unit_number}</option>)}
          </select>
          <select data-testid="finance-opening-type" onChange={(event) => setForm({ ...form, balance_type: event.target.value })} value={form.balance_type}>
            <option value="DEBIT">Debit due</option>
            <option value="CREDIT">Credit advance</option>
          </select>
          <Input data-testid="finance-opening-amount" min="0.01" onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="Amount" type="number" value={form.amount} />
          <Input data-testid="finance-opening-date" onChange={(event) => setForm({ ...form, date: event.target.value })} type="date" value={form.date} />
          <Button data-testid="finance-save-opening" onClick={save}>Post opening balance</Button>
        </div>
        <div className="border-t pt-4">
          <p className="text-sm font-medium">CSV/XLSX opening balance import</p>
          <input accept=".csv,.xlsx" data-testid="finance-opening-import-file" onChange={validateImport} type="file" />
          {job && <div className="mt-2 space-y-2" data-testid="finance-opening-import-summary"><p className="text-sm">{job.summary.valid_rows} valid · {job.summary.errors} errors</p>{job.summary.errors === 0 && <Button data-testid="finance-opening-import-confirm" onClick={confirmImport} size="sm">Confirm import</Button>}</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LedgerDialog({ onOpenChange, person }) {
  const [ledger, setLedger] = useState(null);
  useEffect(() => {
    if (!person?.person_id) {
      setLedger(null);
      return;
    }
    api.get(`/society-finance/ledger/party/${person.person_id}`)
      .then((response) => setLedger(response.data))
      .catch((error) => toast.error(error.response?.data?.detail || "Could not load party ledger"));
  }, [person?.person_id]);
  return <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(person)}><DialogContent className="max-w-3xl" data-testid="finance-ledger-dialog"><DialogHeader><DialogTitle>Operational ledger · {person?.name}</DialogTitle><DialogDescription>Maintenance bills, receipts, and opening balances for this Society party.</DialogDescription></DialogHeader>{!ledger ? <Loading /> : <div className="max-h-[45vh] overflow-auto border"><table className="min-w-[560px] w-full text-sm"><thead className="bg-muted/60"><tr><th className="p-3 text-left">Date</th><th className="p-3 text-left">Record</th><th className="p-3 text-left">Reference</th><th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th><th className="p-3 text-right">Balance</th></tr></thead><tbody>{ledger.rows.map((row, index) => <tr className="border-t" data-testid={`finance-ledger-row-${index}`} key={`${row.reference}-${index}`}><td className="p-3">{row.date}</td><td className="p-3">{row.type}</td><td className="p-3">{row.reference}</td><td className="p-3 text-right">{row.debit ? inr(row.debit) : "—"}</td><td className="p-3 text-right">{row.credit ? inr(row.credit) : "—"}</td><td className="p-3 text-right">{inr(row.balance)}</td></tr>)}</tbody></table></div>}</DialogContent></Dialog>;
}

function ApplyCreditDialog({ bills, onOpenChange, onSaved, receipt }) {
  const [allocations, setAllocations] = useState([{ amount: "", bill_id: "" }]);
  const eligible = bills.filter(
    (bill) => bill.party?.person_id === receipt?.party?.person_id && bill.outstanding > 0,
  );
  const save = async () => {
    try {
      await api.post(`/society-finance/receipts/${receipt.receipt_id}/allocate`, {
        allocations: allocations.filter((item) => item.bill_id && item.amount),
      });
      toast.success("Advance credit allocated to selected bills");
      setAllocations([{ amount: "", bill_id: "" }]);
      onOpenChange(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not allocate credit");
    }
  };
  return <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(receipt)}><DialogContent data-testid="finance-apply-credit-dialog"><DialogHeader><DialogTitle>Apply advance credit</DialogTitle><DialogDescription>{receipt?.receipt_number} has {inr(receipt?.unallocated_credit)} available.</DialogDescription></DialogHeader><div className="grid gap-3">{allocations.map((allocation, index) => <div className="flex gap-2" key={index}><select data-testid={`finance-credit-bill-${index}`} onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, bill_id: event.target.value } : item))} value={allocation.bill_id}><option value="">Choose bill</option>{eligible.map((bill) => <option key={bill.bill_id} value={bill.bill_id}>{bill.bill_number} · {inr(bill.outstanding)}</option>)}</select><Input data-testid={`finance-credit-amount-${index}`} min="0.01" onChange={(event) => setAllocations(allocations.map((item, itemIndex) => itemIndex === index ? { ...item, amount: event.target.value } : item))} placeholder="Amount" type="number" value={allocation.amount} /></div>)}<Button data-testid="finance-add-credit-allocation" onClick={() => setAllocations([...allocations, { amount: "", bill_id: "" }])} size="sm" variant="outline">Add bill</Button><Button data-testid="finance-save-credit-allocation" onClick={save}>Apply credit</Button></div></DialogContent></Dialog>;
}

function AdjustmentDialog({ bill, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ amount: "", direction: "DEBIT", reason: "" });
  const save = async () => {
    try {
      await api.post(`/society-finance/bills/${bill.bill_id}/adjustments`, form);
      toast.success("Adjustment posted without changing original bill charge lines");
      setForm({ amount: "", direction: "DEBIT", reason: "" });
      onOpenChange(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post adjustment");
    }
  };
  return <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(bill)}><DialogContent data-testid="finance-adjustment-dialog"><DialogHeader><DialogTitle>Bill adjustment</DialogTitle><DialogDescription>{bill?.bill_number} remains intact; this creates a separately auditable adjustment.</DialogDescription></DialogHeader><div className="grid gap-3"><select data-testid="finance-adjustment-direction" onChange={(event) => setForm({ ...form, direction: event.target.value })} value={form.direction}><option value="DEBIT">Add debit</option><option value="CREDIT">Apply credit</option></select><Input data-testid="finance-adjustment-amount" min="0.01" onChange={(event) => setForm({ ...form, amount: event.target.value })} placeholder="Amount" type="number" value={form.amount} /><Input data-testid="finance-adjustment-reason" onChange={(event) => setForm({ ...form, reason: event.target.value })} placeholder="Reason" value={form.reason} /><Button data-testid="finance-save-adjustment" onClick={save}>Post adjustment</Button></div></DialogContent></Dialog>;
}

function PreviewDialog({ onOpenChange, onPosted, preview }) {
  const [allowPartial, setAllowPartial] = useState(false);
  useEffect(() => {
    setAllowPartial(false);
  }, [preview?.period?.period_id]);
  const post = async () => {
    try {
      const response = await api.post(
        `/society-finance/periods/${preview.period.period_id}/post`,
        { idempotency_key: newKey(), allow_partial: allowPartial },
      );
      toast.success(
        response.data.idempotent
          ? "Existing posted batch loaded"
          : `${response.data.generated ?? response.data.bill_count} generated · ${response.data.skipped ?? 0} skipped · ${response.data.failed ?? 0} failed`,
      );
      onOpenChange(null);
      onPosted();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not post billing period");
    }
  };

  const errors = preview?.errors || [];
  const summary = preview?.summary;

  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(preview)}>
      <DialogContent className="max-w-5xl" data-testid="finance-preview-dialog">
        <DialogHeader>
          <DialogTitle>{preview?.period?.label} — Generate monthly bills</DialogTitle>
          <DialogDescription>
            {preview?.posted
              ? "These are stable posted bill snapshots."
              : "Review unit-level calculations before the one-time posting action."}
          </DialogDescription>
        </DialogHeader>
        {summary && !preview?.posted && (
          <div className="flex flex-wrap gap-2 text-sm" data-testid="finance-preview-summary">
            <span className="rounded bg-emerald-50 px-3 py-1 text-emerald-800">Generated: {summary.generated}</span>
            <span className="rounded bg-amber-50 px-3 py-1 text-amber-800">Failed: {summary.failed}</span>
            <span className="rounded bg-slate-100 px-3 py-1 text-slate-700">Skipped: {summary.skipped}</span>
            <span className="rounded bg-slate-100 px-3 py-1 text-slate-700">Already billed: {summary.already_billed}</span>
            <span className="rounded bg-slate-100 px-3 py-1 text-slate-700">Total units: {summary.total_units}</span>
          </div>
        )}
        {errors.length > 0 && (
          <section
            className="border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-950"
            data-testid="finance-preview-errors"
          >
            <p className="font-semibold">These Units have missing data or calculation errors:</p>
            <ul className="mt-2 list-disc space-y-1 pl-5">
              {errors.map((error) => (
                <li key={`${error.unit_id}-${error.charge_id}`}>{error.message}</li>
              ))}
            </ul>
            <label className="mt-3 flex items-center gap-2 font-medium">
              <input
                checked={allowPartial}
                data-testid="finance-allow-partial"
                onChange={(event) => setAllowPartial(event.target.checked)}
                type="checkbox"
              />
              Generate valid bills only and skip the failed Units
            </label>
          </section>
        )}
        <div className="max-h-[48vh] overflow-auto border">
          <table className="min-w-[780px] w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-3 text-left">Unit</th>
                <th className="p-3 text-left">Owner</th>
                <th className="p-3 text-left">Charge calculation</th>
                <th className="p-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {preview?.items?.map((item) => (
                <tr
                  className="border-t"
                  data-testid={`finance-preview-item-${item.bill_id || item.unit_id}`}
                  key={item.bill_id || item.unit_id}
                >
                  <td className="p-3">
                    {item.unit_snapshot?.display_unit_code || item.unit_snapshot?.unit_number || item.unit_number}
                  </td>
                  <td className="p-3">{item.owner_snapshot?.display_name || item.party?.name}</td>
                  <td className="p-3 text-xs">
                    {(item.charge_lines || item.lines || []).map((line) => (
                      <div key={line.charge_id}>
                        {line.label}: {line.calculation_method === "PER_QUANTITY"
                          ? `${line.quantity_used} × ${inr(line.rate_used || line.rate)} = ${inr(line.amount)}`
                          : line.basis === "PER_SQFT"
                            ? `${line.area_basis_used?.replaceAll("_", " ") || "Carpet Area"} ${line.area_sqft_used ?? line.unit_area} Sq.Ft. × ${inr(line.rate_used || line.rate)} = ${inr(line.amount)}`
                            : line.calculation_method === "UNIT_TYPE_RATE"
                              ? `Unit type rate ${inr(line.rate_used || line.rate)} = ${inr(line.amount)}`
                              : line.calculation_method === "PERCENTAGE"
                                ? `${line.percentage_used}% of ${line.percentage_base_charge_name} = ${inr(line.amount)}`
                                : line.calculation_method === "VARIABLE"
                                  ? `Variable = ${inr(line.amount)}`
                                  : `Fixed = ${inr(line.amount)}`}
                      </div>
                    ))}
                  </td>
                  <td className="p-3 text-right">{inr(item.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {preview && !preview.posted && (
          <Button
            data-testid="finance-post-period"
            disabled={errors.length > 0 && !allowPartial}
            onClick={post}
          >
            {errors.length > 0 && allowPartial
              ? `Post valid bills (skip ${summary?.failed ?? errors.length} failed)`
              : `Post ${preview.items.length} stable bills`}
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SingleBillDialog({ onOpenChange, onSaved, period, units }) {
  const [unitId, setUnitId] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    setUnitId("");
    setPreview(null);
  }, [period?.period_id]);
  const loadPreview = async (value) => {
    setUnitId(value);
    setPreview(null);
    if (!value) return;
    try {
      const response = await api.get(
        `/society-finance/periods/${period.period_id}/preview`,
        { params: { unit_id: value } },
      );
      const item = (response.data.items || []).find((row) => row.unit_id === value);
      setPreview({ item, errors: response.data.errors || [] });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not preview the unit bill");
    }
  };
  const generate = async () => {
    try {
      const response = await api.post(
        `/society-finance/periods/${period.period_id}/bill-unit`,
        { unit_id: unitId, idempotency_key: newKey() },
      );
      toast.success(
        response.data.idempotent
          ? "This unit bill already exists"
          : `Bill ${response.data.bill_number} generated (${inr(response.data.total)})`,
      );
      onOpenChange(null);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not generate the unit bill");
    }
  };
  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(period)}>
      <DialogContent data-testid="finance-single-bill-dialog">
        <DialogHeader>
          <DialogTitle>Generate single-unit bill · {period?.label}</DialogTitle>
          <DialogDescription>Pick a Unit, review the calculated bill, then generate it for this period.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <select data-testid="finance-single-bill-unit" onChange={(event) => loadPreview(event.target.value)} value={unitId}>
            <option value="">Choose a Unit</option>
            {units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>{unit.unit_number}</option>
            ))}
          </select>
          {preview?.errors?.length > 0 && (
            <div className="border-l-4 border-amber-500 bg-amber-50 p-3 text-sm text-amber-950" data-testid="finance-single-bill-errors">
              {preview.errors.map((error) => <div key={error.charge_id}>{error.message}</div>)}
            </div>
          )}
          {preview?.item && (
            <div className="border bg-muted/20 p-3 text-sm" data-testid="finance-single-bill-preview">
              {(preview.item.lines || preview.item.charge_lines || []).map((line) => (
                <div className="flex justify-between" key={line.charge_id}>
                  <span>{line.label}</span>
                  <span>{inr(line.amount)}</span>
                </div>
              ))}
              <div className="mt-2 flex justify-between border-t pt-2 font-semibold">
                <span>Total</span>
                <span>{inr(preview.item.total)}</span>
              </div>
            </div>
          )}
          <Button
            data-testid="finance-single-bill-generate"
            disabled={!preview?.item || preview?.errors?.length > 0}
            onClick={generate}
          >
            Generate bill
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RulePreviewDialog({ onOpenChange, preview }) {
  const items = preview?.items || [];
  const errors = preview?.errors || [];

  return (
    <Dialog onOpenChange={() => onOpenChange(null)} open={Boolean(preview)}>
      <DialogContent className="max-w-6xl" data-testid="finance-rule-preview-dialog">
        <DialogHeader>
          <DialogTitle>Eligible Unit preview</DialogTitle>
          <DialogDescription>
            {preview?.summary?.eligible_units || 0} eligible · {preview?.summary?.excluded_units || 0}
            {" "}excluded · {preview?.summary?.warnings || 0} warnings. This does not create bills.
          </DialogDescription>
        </DialogHeader>
        {errors.length > 0 && (
          <section className="border-l-4 border-amber-500 bg-amber-50 p-3" data-testid="finance-rule-preview-errors">
            {errors.map((error) => <p className="text-sm" key={`${error.unit_id}-${error.charge_id}`}>{error.message}</p>)}
          </section>
        )}
        <div className="max-h-[52vh] overflow-auto border" data-testid="finance-rule-preview-table">
          <table className="min-w-[1100px] w-full text-sm">
            <thead className="bg-muted/60">
              <tr>
                <th className="p-3 text-left">Unit</th>
                <th className="p-3 text-left">Building / wing</th>
                <th className="p-3 text-left">Type / occupancy</th>
                <th className="p-3 text-left">Owner / addressee</th>
                <th className="p-3 text-left">Qualification</th>
                <th className="p-3 text-right">Expected charge</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const line = item.lines?.[0] || {};
                const quantity = line.quantity_used ? ` · Qty ${line.quantity_used}` : "";
                const area = line.area_sqft_used
                  ? ` · ${line.area_basis_used?.replaceAll("_", " ")} ${line.area_sqft_used}`
                  : "";
                return (
                  <tr className="border-t" data-testid={`finance-rule-preview-unit-${item.unit_id}`} key={item.unit_id}>
                    <td className="p-3">{item.unit_number}</td>
                    <td className="p-3">{item.building || "—"} · {item.wing || "—"}</td>
                    <td className="p-3">{item.unit_type} · {item.occupancy_status}</td>
                    <td className="p-3">{item.owner_snapshot?.display_name || item.party?.name}</td>
                    <td className="p-3 text-xs">
                      {(line.eligibility_snapshot?.reasons || []).join(" + ")}
                      {area}{quantity} · Parking {item.parking_quantity || 0}
                    </td>
                    <td className="p-3 text-right">{inr(line.calculated_amount || item.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Status({ status, testId }) {
  const tone = status === "PAID" || status === "POSTED" || status === "ACTIVE" ? "bg-emerald-100 text-emerald-800" : status === "REVERSED" ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800";
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tone}`} data-testid={testId}>{status}</span>;
}